import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  AuthenticationFlowType,
  DomainPolicyStatus,
} from '../../../../../../typings/api/skymap/rest/v1/.common';
import { ProfileModel } from '../../../../../../typings/api/skymap/rest/v1/profile';
import MicrosoftLogoImage from '../../../../../static/Microsoft_logo.svg?react';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { t } from '../../../../utils/i18-next';
import { ProfileContext, ProfileLoadingState } from '../../../state/profile-state';
import { Button } from '../../button/button';
import { Icon } from '../../icon/icon';
import { Stack } from '../../stack/stack';
import { Tooltip } from '../../tooltip/tooltip';
import { PageProperty } from '../page-property';
import { DisconnectSsoDialog } from './disconnect-sso-form';

type Props = {
  profile?: ProfileModel;
};

const AuthenticationAndSecurity = (props: Props) => {
  const { reloadProfile, state } = useContext(ProfileContext);
  const [showDisconnectSsoDialog, setShowDisconnectSsoDialog] = useState(false);

  const [isLoadingProfile, setIsLoadingProfile] = useState(state === ProfileLoadingState.Loading);

  const isAuthenticationFlowType = (value: unknown): value is AuthenticationFlowType =>
    value === 'PASSWORD' || value === 'MICROSOFT_ENTRA_ID' || value === 'ZITADEL';

  const isDomainPolicyStatus = (value: unknown): value is DomainPolicyStatus => {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const v = value as Record<string, unknown>;
    const validPolicyType =
      v.policyType === 'SSO_REQUIRED' ||
      v.policyType === 'SSO_OPTIONAL' ||
      v.policyType === 'PASSWORD_ONLY';
    return typeof v.domain === 'string' && typeof v.isEnforced === 'boolean' && validPolicyType;
  };

  const rawAuthType: unknown = props.profile?.authenticationFlow?.authFlowType;
  const authenticationFlowType: AuthenticationFlowType | undefined = isAuthenticationFlowType(
    rawAuthType,
  )
    ? rawAuthType
    : undefined;
  const rawDomainPolicyStatus: unknown = props.profile?.domainPolicyStatus;
  const domainPolicyStatus: DomainPolicyStatus | undefined = isDomainPolicyStatus(
    rawDomainPolicyStatus,
  )
    ? rawDomainPolicyStatus
    : undefined;

  const isSwitchDisabled = React.useMemo(() => {
    if (!domainPolicyStatus) {
      return false;
    }
    const isRequirement =
      domainPolicyStatus.isEnforced &&
      (domainPolicyStatus.policyType === 'SSO_REQUIRED' ||
        domainPolicyStatus.policyType === 'PASSWORD_ONLY');
    if (!isRequirement) {
      return false;
    }
    if (domainPolicyStatus.policyType === 'PASSWORD_ONLY') {
      return authenticationFlowType === 'PASSWORD';
    }
    // SSO_REQUIRED
    return authenticationFlowType === 'MICROSOFT_ENTRA_ID';
  }, [domainPolicyStatus, authenticationFlowType]);

  useEffect(() => {
    setIsLoadingProfile(state === ProfileLoadingState.Loading);
  }, [state, setIsLoadingProfile]);

  /* 
    Enabling SSO might trigger an OAuth flow, etc. 
  */
  const handleEnableSSO = () => {
    const authV1Service = SkyMapAxiosServiceFactory.instance.createAuthServiceV1();
    window.location.href = authV1Service.getMicrosoftRedirectUrl({
      query: { purpose: 'connect-existing', userId: props.profile?.id },
    });
  };

  /* 
    Disabling SSO might require user to set a new password 
    or confirm an existing password. 
  */
  const handleDisableSSO = () => {
    setShowDisconnectSsoDialog(true);
  };

  const onDisconnectSsoDialogClosed = React.useCallback(
    (successfullyUpdatedAuthenticationFlow: boolean) => {
      setShowDisconnectSsoDialog(false);
      if (successfullyUpdatedAuthenticationFlow) {
        reloadProfile();
      }
    },
    [setShowDisconnectSsoDialog, reloadProfile],
  );

  const resolveActiveAuthenticationIcon = () => (
    <ActiveAuthenticationFlow alignItems="center" direction="row" spacing={'0.5em'}>
      {authenticationFlowType === 'PASSWORD' ? (
        <Icon icon={['fad', 'key']} size="xl" />
      ) : (
        <StyledMicrosoftLogoWrapper>
          <MicrosoftLogoImage />
        </StyledMicrosoftLogoWrapper>
      )}
      {authenticationFlowType === 'PASSWORD' ? t('password', { ns: 'profile' }) : 'Microsoft'}
    </ActiveAuthenticationFlow>
  );

  const resolveSsoButtonText = () =>
    authenticationFlowType === 'PASSWORD'
      ? t('sections.authentication.properties.enableSSO', { ns: 'profile' })
      : t('sections.authentication.properties.disableSSO', { ns: 'profile' });

  if (authenticationFlowType === 'ZITADEL') {
    return (
      <AuthContainer {...props} spacing={'0.5em'}>
        <Stack direction="row">
          <PageProperty
            isLoading={isLoadingProfile}
            title={t('sections.authentication.properties.authenticationMethod', { ns: 'profile' })}
            value={t('sections.authentication.properties.managedOnSkyMapId', { ns: 'profile' })}
          />
        </Stack>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer {...props} spacing={'0.5em'}>
      <Stack direction="row">
        <PageProperty
          isLoading={isLoadingProfile}
          title={t('sections.authentication.properties.authenticationMethod', { ns: 'profile' })}
          value={props.profile && resolveActiveAuthenticationIcon()}
        />
      </Stack>
      {!isLoadingProfile && (
        <Stack alignItems="end" direction="row-reverse">
          {isSwitchDisabled && domainPolicyStatus?.policyType === 'SSO_REQUIRED' ? (
            <Tooltip
              content={t('sections.authentication.properties.ssoRequiredDisabledTooltip', {
                ns: 'profile',
                domain: domainPolicyStatus?.domain,
              })}
              title={t('sections.authentication.properties.disableSSO', { ns: 'profile' })}
              wrapChildren={true}
            >
              <Button
                alignSelf="end"
                color={'primary'}
                disabled={true}
                variant={'contained'}
                onClick={authenticationFlowType === 'PASSWORD' ? handleEnableSSO : handleDisableSSO}
              >
                {resolveSsoButtonText()}
              </Button>
            </Tooltip>
          ) : (
            <Button
              alignSelf="end"
              color={'primary'}
              disabled={isSwitchDisabled}
              variant={'contained'}
              onClick={authenticationFlowType === 'PASSWORD' ? handleEnableSSO : handleDisableSSO}
            >
              {resolveSsoButtonText()}
            </Button>
          )}
        </Stack>
      )}

      {showDisconnectSsoDialog && <DisconnectSsoDialog onClose={onDisconnectSsoDialogClosed} />}
    </AuthContainer>
  );
};

const AuthContainer = styled(Stack)`
  width: 100%;
`;

const ActiveAuthenticationFlow = styled(Stack)`
  background-color: ${(props) => props.theme.color.gray.lightest};
  box-shadow: 0 1px 5px ${(props) => props.theme.color.gray.medium};

  padding: 0.5em;
  border-radius: 8%;

  max-width: fit-content;
`;

const StyledMicrosoftLogoWrapper = styled.div`
  margin-bottom: -4px;
  > svg {
    width: 1.5em;
    height: 1.5em;
    margin-right: 8px;
  }
`;

export { AuthenticationAndSecurity };
